import { BaseGrid } from "./BaseGrid";
import { ICommonColumnDefinition } from "./Interfaces";
import { IControl } from "Controls/Interfaces";
import { CoreContext } from "Core";
import { EditModes, DisplayModes } from "Enums";
import { IDataRow, IFormData } from "Entities";

/**
 * Base class for forms.
 * @hidden
 */
export abstract class BaseForm extends BaseGrid {
    /**
     * @hidden
     */
    public dmsColumnDictionary: { [columnName: string]: ICommonColumnDefinition } = {};

    /**
     * @hidden
     */
    public dmsControlDictionary: { [controlId: number]: IControl } = {};

    /**
     * @hidden
     */
    public dmsColumnVisibility: { [columnName: string]: boolean } = {};

    /**
     * @hidden
     */
    public dmsControlTemplateDictionary: { [controlId: number]: string } = {};

    /**
     * @hidden
     */
    protected editMode: EditModes = EditModes.Edit;

    /**
     * Holds the default values of each control.
     * @hidden
     */
    protected fields = {};

    /**
     * @hidden
     */
    protected _viewModel: kendo.data.ObservableObject;

    /**
     * Initialize columns for this form.
     * @param columns List of columns.
     * @param fields Fields which will be filled.
     * @hidden
     */
    protected initializeColumns(columns: ICommonColumnDefinition[], fields: {}): {} {
        for (let col = 0, colLen = columns.length; col < colLen; col++) {
            // empty cells are perfectly fine
            let column = columns[col];
            if (column == null) {
                continue;
            }

            this.dmsColumnDictionary[column.ColumnName] = column;
            for (let ctrIndex = 0, ctrLen = column.Controls.length; ctrIndex < ctrLen; ctrIndex++) {
                let ctr = column.Controls[ctrIndex];

                // Initialize control
                let ctl = CoreContext.Instance.initializeControl(ctr, this.textBlockCategory);

                // Initialize filter fields for view model
                let fieldName = this.getControlFieldName(ctr);

                if (fieldName) {
                    if (fields[fieldName] == null) {
                        // field not already existing
                        fields[fieldName] = ctl.getDefaultValue();
                    }
                }
                this.dmsControlDictionary[ctr.ControlID] = ctl;
            }
        }
        return fields;
    }

    /**
     * Initialize control templates for all columns.
     * @param data 
     */
    protected initializeControlTemplates(data: IDataRow) {
        let displayMode = this.editMode == EditModes.Add ? DisplayModes.Add : DisplayModes.Edit;

        let columns = Object.keys(this.dmsColumnDictionary);
        for (let colIdx = 0, colLen = columns.length; colIdx < colLen; colIdx++) {
            let columnName = columns[colIdx];
            let column = this.dmsColumnDictionary[columnName];

            let hasVisibleControls = false;

            for (let ctrIndex = 0, ctrLen = column.Controls.length; ctrIndex < ctrLen; ctrIndex++) {
                let ctr = column.Controls[ctrIndex];
                let ctl = this.dmsControlDictionary[ctr.ControlID];
                let fieldName = this.getControlFieldName(ctr);
                let bindingField = fieldName || null;

                ctl.modifyForDataRow(data);
                let controlTemplate = ctl.getTemplate(displayMode, bindingField);

                this.dmsControlTemplateDictionary[ctr.ControlID] = controlTemplate ? controlTemplate.template : "";
                if (controlTemplate) {
                    if (controlTemplate.template) {
                        hasVisibleControls = true;
                    }
                    if (controlTemplate.modelExtension) {
                        $.extend(data, controlTemplate.modelExtension);
                    }
                }
            }
            this.dmsColumnVisibility[column.ColumnName] = hasVisibleControls;
        }
    }

    /**
     * Get the current data.
     * @since 4.0.0
     */
    public getData(): IFormData {
        if (this._viewModel) {
            let data = this._viewModel.get("");

            if (this.editMode == EditModes.Add) {
                data = $.extend(data, this.fixedValues);
            }
            return data;
        }
        return null;
    }

    /**
     * Triggered when a control requests to resolve a script.
     * @param ctrl Control which requested the resolving.
     * @param script Scripts which needs to be resolved.
     */
    protected onResolveScript(ctrl: IControl, script: string): string {
        let data = this.getData();
        return super.resolve(script, data);
    }
}
